import type { FocusableElement } from '@react-types/shared';

import { ButtonLink, Icon, ModalHeader } from '@coop/components';
import { UserIcon, CheckIcon, XIcon } from '@coop/icons';
import classnames from 'classnames';

import { appConfig } from '../../../../../appConfig';
import {
    useIsUserAMember,
    useIsUserCompany,
    useUserProfile,
} from '../../../../common/hooks/useUserProfile';
import { useMyCoopSettingsPageQuery } from '../../../../myCoop/hooks/useMyCoopSettingsPageQuery';
import styles from './MenuHeaderBonus2026.module.scss';

interface MenuChildrenProps {
    myBonusOverviewPageUrl: string;
    handleLinkClick: (e: React.MouseEvent<FocusableElement>) => void;
    navigationSlot?: React.ReactNode;
}

const MenuHeaderBonus2026 = ({ navigationSlot }: MenuChildrenProps) => {
    const isMember = useIsUserAMember();
    const isCompany = useIsUserCompany();
    const profile = useUserProfile();

    const { isShoppingForAnother } = appConfig.coopUserSettings;

    const { data: settingsTexts } = useMyCoopSettingsPageQuery([
        'notMemberHeadline',
        'notMemberPoint1',
        'notMemberPoint2',
        'notMemberPoint3',
        'notMemberPoint1Checked',
        'notMemberPoint2Checked',
        'notMemberPoint3Checked',
        'notMemberPoint1CheckedText',
        'notMemberPoint1UnCheckedText',
        'notMemberPoint2CheckedText',
        'notMemberPoint2UnCheckedText',
        'notMemberPoint3CheckedText',
        'notMemberPoint3UnCheckedText',
        'notMemberButtonText',
        'notMemberButtonContentReference',
    ]);

    if (!settingsTexts) {
        return null;
    }

    return (
        <div
            className={classnames(
                styles.FlyinHeaderBonus2026,
                isCompany || isShoppingForAnother ? styles.HeaderCompany : '',
                !isMember && !isCompany ? styles.NotBonusMember : '',
            )}
        >
            <div
                className={classnames(styles.HeaderContent, {
                    [styles.NotBonusMember]: !isMember && !isCompany,
                })}
            >
                {isMember ? (
                    <>
                        <div className={styles.UserIconWrapper}>
                            <Icon
                                icon={<UserIcon />}
                                size="small"
                                color="primary"
                                className={styles.UserIcon}
                            />
                        </div>
                        <ModalHeader className={styles.Headline}>
                            Hej{profile && ` ${profile.firstName}`}!
                        </ModalHeader>
                    </>
                ) : (
                    <div>
                        {isCompany || isShoppingForAnother ? (
                            <ModalHeader className={styles.HeadlineCompany}>
                                {isCompany ? 'Företagskonto' : ''}
                            </ModalHeader>
                        ) : (
                            <>
                                <ModalHeader
                                    className={classnames(styles.Headline, {
                                        [styles.notMemberHeadline]: !isMember,
                                    })}
                                >
                                    {settingsTexts.notMemberHeadline
                                        ? settingsTexts.notMemberHeadline
                                        : 'Inloggad (ej medlem)'}
                                </ModalHeader>
                                <div className={styles.NotMemberPointsContainer}>
                                    {settingsTexts.notMemberPoint1 && (
                                        <div className={styles.NotMemberPoints}>
                                            <div className={styles.IconWrapper}>
                                                <Icon
                                                    icon={<CheckIcon />}
                                                    size="small"
                                                    color="black"
                                                />
                                            </div>
                                            <div
                                                aria-hidden
                                                className={styles.NotMemberPointsTextWrapper}
                                            >
                                                {settingsTexts.notMemberPoint1}
                                            </div>
                                            <span className="u-hiddenVisually">
                                                {settingsTexts.notMemberPoint1Checked
                                                    ? settingsTexts.notMemberPoint1CheckedText
                                                    : settingsTexts.notMemberPoint1UnCheckedText}
                                            </span>
                                        </div>
                                    )}
                                    {settingsTexts.notMemberPoint2 && (
                                        <div className={styles.NotMemberPoints}>
                                            <div className={styles.IconWrapper}>
                                                <Icon icon={<XIcon />} size="small" color="black" />
                                            </div>
                                            <div
                                                aria-hidden
                                                className={styles.NotMemberPointsTextWrapper}
                                            >
                                                {settingsTexts.notMemberPoint2}
                                            </div>
                                            <span className="u-hiddenVisually">
                                                {settingsTexts.notMemberPoint2Checked
                                                    ? settingsTexts.notMemberPoint2CheckedText
                                                    : settingsTexts.notMemberPoint2UnCheckedText}
                                            </span>
                                        </div>
                                    )}
                                    {settingsTexts.notMemberPoint3 && (
                                        <div className={styles.NotMemberPoints}>
                                            <div className={styles.IconWrapper}>
                                                <Icon icon={<XIcon />} size="small" color="black" />
                                            </div>
                                            <div
                                                aria-hidden
                                                className={styles.NotMemberPointsTextWrapper}
                                            >
                                                {settingsTexts.notMemberPoint3}
                                            </div>
                                            <span className="u-hiddenVisually">
                                                {settingsTexts.notMemberPoint3Checked
                                                    ? settingsTexts.notMemberPoint3CheckedText
                                                    : settingsTexts.notMemberPoint3UnCheckedText}
                                            </span>
                                        </div>
                                    )}
                                    {settingsTexts.notMemberButtonText &&
                                        settingsTexts.notMemberButtonContentReference?.url && (
                                            <div className={styles.NotAMemberButtonContainer}>
                                                <ButtonLink
                                                    href={
                                                        settingsTexts
                                                            .notMemberButtonContentReference.url
                                                    }
                                                    theme="subtle"
                                                    fullWidth
                                                >
                                                    {settingsTexts.notMemberButtonText}
                                                </ButtonLink>
                                            </div>
                                        )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
            <div className={styles.Navigation}>{navigationSlot}</div>
        </div>
    );
};

export default MenuHeaderBonus2026;
